import { useCallback, useEffect, useState } from "react";
import { Activity, TrendingUp, Loader2, RefreshCw } from "lucide-react";
import {
  LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
  BarChart, Bar, Legend,
} from "recharts";
import { toast } from "sonner";
import { partnerApi, formatPartnerError } from "./partnerApi";
import "./PartnerPortal.css";

const WINDOWS = [
  { label: "7d", days: 7 },
  { label: "30d", days: 30 },
  { label: "90d", days: 90 },
];

export default function PartnerAnalytics() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(30);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await partnerApi.get(`/partner/portal/analytics?window_days=${days}`);
      setData(data);
    } catch (e) {
      toast.error(formatPartnerError(e));
    } finally { setLoading(false); }
  }, [days]);
  useEffect(() => { load(); }, [load]);

  return (
    <div data-testid="pp-analytics">
      <h1 className="hw-pp__page-title">
        <TrendingUp className="w-5 h-5"
                     style={{ display: "inline", verticalAlign: "text-bottom", marginRight: "0.4rem" }} />
        API Analytics
      </h1>
      <p className="hw-pp__page-sub">
        Requests, response health, webhook activity, and top integrations for your organization.
      </p>

      <div style={{ display: "flex", gap: "0.35rem", marginBottom: "0.75rem" }}>
        {WINDOWS.map((w) => (
          <button key={w.days}
                   className={`hw-pp__btn ${days === w.days ? "hw-pp__btn--primary" : ""}`}
                   onClick={() => setDays(w.days)}
                   data-testid={`pp-an-window-${w.label}`}>
            {w.label}
          </button>
        ))}
        <button className="hw-pp__btn" onClick={load} style={{ marginLeft: "auto" }}>
          <RefreshCw className="w-3.5 h-3.5" /> Refresh
        </button>
      </div>

      {loading && !data && (
        <div style={{ color: "#5EEAD4" }}>
          <Loader2 className="w-4 h-4 animate-spin" style={{ display: "inline" }} /> loading…
        </div>
      )}

      {data && (
        <>
          <div className="hw-pp__stat-grid">
            <Stat label="Total API requests" value={data.totals.requests}
                    testid="pp-an-total-requests" />
            <Stat label="Webhook deliveries" value={data.totals.webhook_deliveries}
                    testid="pp-an-deliveries" />
            <Stat label="Success rate"
                    value={`${data.totals.delivery_success_rate_pct}%`}
                    testid="pp-an-success-rate" />
            <Stat label="Error rate"
                    value={`${data.totals.error_rate_pct}%`}
                    testid="pp-an-error-rate" />
            <Stat label="DLQ items" value={data.totals.dead}
                    testid="pp-an-dlq" />
            <Stat label="Avg latency"
                    value={data.totals.avg_delivery_seconds != null
                              ? `${data.totals.avg_delivery_seconds}s` : "—"}
                    testid="pp-an-latency" />
            <Stat label="Auth failures" value={data.totals.auth_failures}
                    testid="pp-an-auth-failures" />
            <Stat label="Active keys" value={data.totals.active_keys}
                    testid="pp-an-active-keys" />
          </div>

          <div className="hw-pp__panel" data-testid="pp-an-daily-chart">
            <h3>
              <span>Daily activity</span>
              <small>Webhook deliveries + failures + auth failures over {days} days</small>
            </h3>
            <ResponsiveContainer width="100%" height={220}>
              <LineChart data={data.daily_series}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(94,234,212,0.1)" />
                <XAxis dataKey="date" stroke="#64748B"
                        tick={{ fontSize: 10 }}
                        tickFormatter={(d) => d.slice(5)} />
                <YAxis stroke="#64748B" tick={{ fontSize: 10 }} />
                <Tooltip
                  contentStyle={{ background: "#0F172A",
                                  border: "1px solid rgba(94,234,212,0.2)",
                                  borderRadius: 6, fontSize: 12 }}
                  labelStyle={{ color: "#F8FAFC" }}
                />
                <Legend wrapperStyle={{ fontSize: 11, color: "#94A3B8" }} />
                <Line type="monotone" dataKey="webhook_deliveries"
                       stroke="#14B8A6" strokeWidth={2} dot={false}
                       name="Deliveries" />
                <Line type="monotone" dataKey="webhook_failures"
                       stroke="#EF4444" strokeWidth={2} dot={false}
                       name="Failures" />
                <Line type="monotone" dataKey="auth_failures"
                       stroke="#F59E0B" strokeWidth={2} dot={false}
                       name="Auth failures" />
              </LineChart>
            </ResponsiveContainer>
          </div>

          <div className="hw-pp__panel" data-testid="pp-an-top-keys">
            <h3>Usage by integration (API key)</h3>
            {data.top_keys.length === 0 ? (
              <div style={{ color: "#64748B", fontSize: "0.85rem" }}>No key activity yet.</div>
            ) : (
              <ResponsiveContainer width="100%" height={180}>
                <BarChart data={data.top_keys} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(94,234,212,0.1)" />
                  <XAxis type="number" stroke="#64748B" tick={{ fontSize: 10 }} />
                  <YAxis type="category" dataKey="name" stroke="#64748B"
                          tick={{ fontSize: 10 }} width={130} />
                  <Tooltip
                    contentStyle={{ background: "#0F172A",
                                    border: "1px solid rgba(94,234,212,0.2)",
                                    borderRadius: 6, fontSize: 12 }}
                  />
                  <Bar dataKey="requests" fill="#14B8A6" />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>

          <div className="hw-pp__panel" data-testid="pp-an-top-events">
            <h3>Top events (last {days}d)</h3>
            {data.top_events.length === 0 ? (
              <div style={{ color: "#64748B", fontSize: "0.85rem" }}>No events yet.</div>
            ) : (
              <table className="hw-pp__table">
                <thead>
                  <tr><th>Event</th><th style={{ width: 140 }}>Deliveries</th></tr>
                </thead>
                <tbody>
                  {data.top_events.map((e) => (
                    <tr key={e.event}>
                      <td className="hw-pp__mono">{e.event}</td>
                      <td>{e.deliveries}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          <div style={{ fontSize: "0.7rem", color: "#64748B", textAlign: "right" }}>
            Computed at {(data.computed_at || "").replace("T", " ").slice(0, 19)} UTC
          </div>
        </>
      )}
    </div>
  );
}

function Stat({ label, value, testid }) {
  return (
    <div className="hw-pp__stat" data-testid={testid}>
      <div className="hw-pp__stat-label">{label}</div>
      <div className="hw-pp__stat-value">
        {typeof value === "number" ? new Intl.NumberFormat().format(value) : value}
      </div>
    </div>
  );
}
